import { useEffect, useMemo, useRef, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  ArrowDown,
  ArrowUp,
  ArrowUpDown,
  Building2,
  EyeOff,
  Loader2,
  Package,
  RotateCcw,
  Search,
  X,
} from 'lucide-react';
import { toast } from 'sonner';
import { PageGuard } from '@components/PageGuard';
import { PermissionGate } from '@components/PermissionGate';
import { ProductDetailModal } from '@components/businesses/ProductDetailModal';
import { businessesService } from '@services/businesses.service';
import {
  productsService,
  type AdminProductListParams,
  type AdminProductRow,
} from '@services/products.service';
import { cn, formatCurrency } from '@lib/utils';

type StatusFilter = 'all' | 'active' | 'inactive';
type SortKey = 'createdAt' | 'name' | 'price';

const PAGE_SIZE = 25;

const STATUS_CHIPS: { value: StatusFilter; label: string }[] = [
  { value: 'all', label: 'All' },
  { value: 'active', label: 'Active' },
  { value: 'inactive', label: 'Draft' },
];

function apiMessage(err: unknown, fallback: string): string {
  if (err && typeof err === 'object') {
    const e = err as { message?: unknown };
    if (typeof e.message === 'string' && e.message.trim()) return e.message;
  }
  return fallback;
}

function formatDate(iso?: string | null) {
  if (!iso) return '-';
  return new Date(iso).toLocaleDateString('en-KE', { day: '2-digit', month: 'short', year: 'numeric' });
}

/** Debounce a fast-changing value (search box). */
function useDebounced<T>(value: T, ms: number): T {
  const [v, setV] = useState(value);
  useEffect(() => {
    const t = setTimeout(() => setV(value), ms);
    return () => clearTimeout(t);
  }, [value, ms]);
  return v;
}

// ── Merchant typeahead ──────────────────────────────────────────────────────
interface MerchantPickerProps {
  businessId: string;
  selectedName: string | null;
  onSelect: (id: string, name: string) => void;
  onClear: () => void;
}

const MerchantPicker = ({ businessId, selectedName, onSelect, onClear }: MerchantPickerProps) => {
  const [text, setText] = useState('');
  const [open, setOpen] = useState(false);
  const debounced = useDebounced(text.trim(), 300);
  const boxRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const onDoc = (e: MouseEvent) => {
      if (boxRef.current && !boxRef.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, []);

  const { data: results = [], isFetching } = useQuery({
    queryKey: ['products-review-merchant-search', debounced],
    queryFn: async () => {
      const res: any = await businessesService.list({ page: 1, limit: 8, search: debounced });
      const rows: any[] = Array.isArray(res) ? res : (res?.content ?? res?.data ?? []);
      return rows.map((b) => ({ id: (b.id ?? b.businessId) as string, name: b.name as string }));
    },
    enabled: open && !businessId,
    staleTime: 30_000,
  });

  if (businessId) {
    return (
      <span className="inline-flex items-center gap-1.5 rounded-full bg-[var(--bg-base)] border border-[var(--border-subtle)] pl-3 pr-1.5 py-1.5 text-[13px] text-[var(--text-primary)]">
        <Building2 className="w-3.5 h-3.5 text-[var(--text-muted)]" strokeWidth={1.5} />
        {selectedName ?? 'Merchant'}
        <button
          type="button"
          onClick={onClear}
          aria-label="Clear merchant filter"
          className="rounded-full p-0.5 hover:bg-white text-[var(--text-muted)]"
        >
          <X className="w-3.5 h-3.5" />
        </button>
      </span>
    );
  }

  return (
    <div ref={boxRef} className="relative">
      <input
        value={text}
        onChange={(e) => { setText(e.target.value); setOpen(true); }}
        onFocus={() => setOpen(true)}
        placeholder="Filter by merchant"
        className="w-52 rounded-full border border-[var(--border-subtle)] bg-white px-4 py-2 text-[13px] text-[var(--text-primary)] placeholder:text-[var(--text-muted)] focus:outline-none"
      />
      {open && (
        <div className="absolute z-20 mt-2 w-72 rounded-2xl bg-white shadow-soft border border-[var(--border-subtle)] p-1.5 max-h-72 overflow-auto">
          {isFetching && <p className="px-3 py-2 text-[13px] text-[var(--text-muted)]">Searching...</p>}
          {!isFetching && results.length === 0 && (
            <p className="px-3 py-2 text-[13px] text-[var(--text-muted)]">No merchants found.</p>
          )}
          {results.map((b) => (
            <button
              key={b.id}
              type="button"
              onClick={() => { onSelect(b.id, b.name); setText(''); setOpen(false); }}
              className="w-full text-left rounded-xl px-3 py-2 text-[13px] text-[var(--text-primary)] hover:bg-[var(--bg-base)]"
            >
              {b.name}
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

// ── Sortable header ─────────────────────────────────────────────────────────
interface SortThProps {
  label: string;
  sortKey: SortKey;
  active: SortKey;
  order: 'asc' | 'desc';
  onSort: (k: SortKey) => void;
  align?: 'left' | 'right';
}

const SortTh = ({ label, sortKey, active, order, onSort, align = 'left' }: SortThProps) => {
  const isActive = active === sortKey;
  const Icon = !isActive ? ArrowUpDown : order === 'asc' ? ArrowUp : ArrowDown;
  return (
    <th className={cn('py-2.5 pr-4 text-[12px] font-semibold uppercase tracking-[0.08em] text-[var(--text-muted)]', align === 'right' ? 'text-right' : 'text-left')}>
      <button
        type="button"
        onClick={() => onSort(sortKey)}
        className={cn('inline-flex items-center gap-1 uppercase tracking-[0.08em]', isActive && 'text-[var(--text-primary)]')}
      >
        {label}
        <Icon className="w-3 h-3" strokeWidth={1.75} />
      </button>
    </th>
  );
};

const Th = ({ children, align = 'left' }: { children: React.ReactNode; align?: 'left' | 'right' | 'center' }) => (
  <th className={cn('py-2.5 pr-4 text-[12px] font-semibold uppercase tracking-[0.08em] text-[var(--text-muted)]', `text-${align}`)}>
    {children}
  </th>
);

/**
 * PRODUCTS REVIEW - reactive oversight of every product across merchants.
 * Listing is NOT gated here: merchants publish freely; ops can take down
 * (unpublish) or restore a product. Read → FastAPI, takedown/restore → Spring Boot.
 */
export const ProductsReviewPage = () => (
  <PageGuard permission="products.read">
    <ProductsReviewInner />
  </PageGuard>
);

const ProductsReviewInner = () => {
  const queryClient = useQueryClient();
  const [params, setParams] = useSearchParams();

  const q = params.get('q') ?? '';
  const businessId = params.get('businessId') ?? '';
  const businessName = params.get('merchant');
  const branchId = params.get('branchId') ?? '';
  const status = (params.get('status') as StatusFilter) || 'all';
  const sort = (params.get('sort') as SortKey) || 'createdAt';
  const order = (params.get('order') as 'asc' | 'desc') || 'desc';
  const page = Math.max(1, Number(params.get('page') ?? 1) || 1);

  const [searchInput, setSearchInput] = useState(q);
  const debouncedSearch = useDebounced(searchInput.trim(), 350);
  const [selected, setSelected] = useState<{ row: AdminProductRow; full: any } | null>(null);
  const [unpublishTarget, setUnpublishTarget] = useState<AdminProductRow | null>(null);
  const [reason, setReason] = useState('');

  /** Merge changes into the URL; defaults are dropped to keep links clean. */
  const update = (patch: Record<string, string | null>, resetPage = true) => {
    const next = new URLSearchParams(params);
    for (const [k, v] of Object.entries(patch)) {
      if (v == null || v === '') next.delete(k);
      else next.set(k, v);
    }
    if (resetPage && !('page' in patch)) next.delete('page');
    setParams(next, { replace: true });
  };

  // Push the debounced search into the URL.
  useEffect(() => {
    if (debouncedSearch !== q) update({ q: debouncedSearch || null });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedSearch]);

  const listParams: AdminProductListParams = {
    q: q || undefined,
    businessId: businessId || undefined,
    branchId: branchId || undefined,
    status,
    sort,
    order,
    page,
    limit: PAGE_SIZE,
  };

  const { data, isLoading, isFetching, error } = useQuery({
    queryKey: ['admin-products', listParams],
    queryFn: () => productsService.adminList(listParams),
    placeholderData: (prev) => prev,
  });

  const { data: branches = [] } = useQuery({
    queryKey: ['business-branches', businessId],
    queryFn: () => businessesService.listBranches(businessId),
    enabled: !!businessId,
  });

  const rows = data?.data ?? [];
  const total = data?.pagination?.total ?? 0;
  const pageCount = Math.max(1, Math.ceil(total / PAGE_SIZE));

  const refresh = () => queryClient.invalidateQueries({ queryKey: ['admin-products'] });

  const unpublishMutation = useMutation({
    mutationFn: ({ id, why }: { id: string; why?: string }) => productsService.adminUnpublish(id, why),
    onSuccess: () => {
      toast.success('Product unpublished');
      setUnpublishTarget(null);
      setReason('');
      refresh();
    },
    onError: (e) => toast.error(apiMessage(e, 'Could not unpublish product')),
  });

  const publishMutation = useMutation({
    mutationFn: (id: string) => productsService.adminPublish(id),
    onSuccess: () => { toast.success('Product restored'); refresh(); },
    onError: (e) => toast.error(apiMessage(e, 'Could not restore product')),
  });

  const onSort = (k: SortKey) => {
    if (k === sort) update({ order: order === 'asc' ? 'desc' : 'asc' });
    else update({ sort: k === 'createdAt' ? null : k, order: k === 'name' ? 'asc' : null });
  };

  const openRow = async (row: AdminProductRow) => {
    // The list row is thin; try the full product for the modal, fall back to the row.
    let full: any = row;
    try {
      const f = await businessesService.getProduct(row.businessId, row.id);
      full = { ...row, ...f, id: f?.id ?? f?.productId ?? row.id };
    } catch {
      /* no business access for this user: show what the list has */
    }
    setSelected({ row, full });
  };

  const hasFilters = useMemo(
    () => !!(q || businessId || branchId || status !== 'all'),
    [q, businessId, branchId, status],
  );

  return (
    <div className="space-y-6">
      <div>
        <h1 className="font-display text-[28px] tracking-tight text-[var(--text-primary)]">Products</h1>
        <p className="text-[14px] text-[var(--text-muted)] mt-1">
          Every product across all merchants. Merchants publish on their own; take down anything that should not be live.
        </p>
      </div>

      <div className="rounded-3xl bg-white shadow-soft p-6">
        {/* Filters */}
        <div className="flex flex-wrap items-center gap-3 mb-5">
          <div className="relative">
            <Search className="w-4 h-4 text-[var(--text-muted)] absolute left-3.5 top-1/2 -translate-y-1/2" strokeWidth={1.5} />
            <input
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              placeholder="Search name, SKU or slug"
              className="w-64 rounded-full border border-[var(--border-subtle)] bg-white pl-10 pr-4 py-2 text-[13px] text-[var(--text-primary)] placeholder:text-[var(--text-muted)] focus:outline-none"
            />
          </div>

          <MerchantPicker
            businessId={businessId}
            selectedName={businessName}
            onSelect={(id, name) => update({ businessId: id, merchant: name, branchId: null })}
            onClear={() => update({ businessId: null, merchant: null, branchId: null })}
          />

          {businessId && (
            <select
              value={branchId}
              onChange={(e) => update({ branchId: e.target.value || null })}
              className="rounded-full border border-[var(--border-subtle)] bg-white px-4 py-2 text-[13px] text-[var(--text-primary)] focus:outline-none"
            >
              <option value="">All branches</option>
              {(Array.isArray(branches) ? branches : []).map((b: any) => (
                <option key={b.id} value={b.id}>{b.name}</option>
              ))}
            </select>
          )}

          <div className="flex items-center gap-1.5 ml-auto">
            {STATUS_CHIPS.map((c) => (
              <button
                key={c.value}
                type="button"
                onClick={() => update({ status: c.value === 'all' ? null : c.value })}
                className={cn(
                  'rounded-full px-4 py-1.5 text-[13px] font-medium transition-colors',
                  status === c.value
                    ? 'bg-[var(--text-primary)] text-white'
                    : 'bg-[var(--bg-base)] text-[var(--text-secondary)] hover:text-[var(--text-primary)]',
                )}
              >
                {c.label}
              </button>
            ))}
          </div>
        </div>

        <div className="flex items-center justify-between mb-3 text-[13px] text-[var(--text-muted)]">
          <span>
            {isLoading ? 'Loading...' : `${total.toLocaleString()} product${total === 1 ? '' : 's'}`}
            {isFetching && !isLoading && ' · updating...'}
          </span>
          {hasFilters && (
            <button
              type="button"
              onClick={() => { setSearchInput(''); setParams(new URLSearchParams(), { replace: true }); }}
              className="font-medium text-[var(--text-primary)] hover:underline"
            >
              Clear filters
            </button>
          )}
        </div>

        {error && (
          <p className="text-[13px] text-[var(--state-danger)] py-4">{apiMessage(error, 'Failed to load products.')}</p>
        )}

        {!isLoading && !error && rows.length === 0 && (
          <p className="text-[14px] text-[var(--text-muted)] py-10 text-center">No products match these filters.</p>
        )}

        {rows.length > 0 && (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-[var(--bg-base)] border-b border-[var(--border-subtle)]">
                <tr>
                  <SortTh label="Product" sortKey="name" active={sort} order={order} onSort={onSort} />
                  <Th>Merchant</Th>
                  <Th>Branch</Th>
                  <SortTh label="Price" sortKey="price" active={sort} order={order} onSort={onSort} align="right" />
                  <Th align="center">Status</Th>
                  <Th align="right">Stock</Th>
                  <SortTh label="Created" sortKey="createdAt" active={sort} order={order} onSort={onSort} />
                  <Th align="right">Actions</Th>
                </tr>
              </thead>
              <tbody className="divide-y divide-[var(--border-subtle)]">
                {rows.map((p) => (
                  <tr
                    key={p.id}
                    onClick={() => openRow(p)}
                    className="hover:bg-[var(--bg-base)] transition-colors cursor-pointer"
                  >
                    <td className="py-3 pr-4 pl-3">
                      <div className="flex items-center gap-3">
                        {p.primaryImage ? (
                          <img
                            src={p.primaryImage}
                            alt=""
                            className="w-10 h-10 rounded-xl object-cover bg-[var(--bg-base)] flex-shrink-0"
                            onError={(e) => { (e.currentTarget as HTMLImageElement).style.display = 'none'; }}
                          />
                        ) : (
                          <div className="w-10 h-10 rounded-xl bg-[var(--bg-base)] flex items-center justify-center flex-shrink-0">
                            <Package className="w-4 h-4 text-[var(--text-muted)]" strokeWidth={1.5} />
                          </div>
                        )}
                        <div className="min-w-0">
                          <div className="font-medium text-[var(--text-primary)] truncate max-w-[260px]">{p.name}</div>
                          <div className="text-[13px] text-[var(--text-muted)] mt-0.5 truncate">
                            {[p.sku && `SKU ${p.sku}`, p.categoryName].filter(Boolean).join(' · ') || '-'}
                          </div>
                        </div>
                      </div>
                    </td>
                    <td className="py-3 pr-4 text-[14px]" onClick={(e) => e.stopPropagation()}>
                      <Link to={`/businesses/${p.businessId}`} className="text-[var(--text-primary)] font-medium hover:underline">
                        {p.businessName}
                      </Link>
                      {p.businessStatus && p.businessStatus !== 'active' && (
                        <div className="text-[12px] text-[var(--state-warning)] capitalize">{p.businessStatus}</div>
                      )}
                    </td>
                    <td className="py-3 pr-4 text-[14px] text-[var(--text-secondary)]">{p.branchName ?? '-'}</td>
                    <td className="py-3 pr-4 text-right text-[14px] font-medium text-[var(--text-primary)]">
                      {formatCurrency(Number(p.basePrice ?? 0))}
                    </td>
                    <td className="py-3 pr-4 text-center">
                      <span className={cn(
                        'rounded-full px-3 py-1 text-[12px] font-semibold',
                        p.isActive
                          ? 'bg-[var(--state-success-bg)] text-[var(--state-success)]'
                          : 'bg-[var(--bg-base)] text-[var(--text-muted)]',
                      )}>
                        {p.isActive ? 'Active' : 'Draft'}
                      </span>
                    </td>
                    <td className="py-3 pr-4 text-right text-[14px] font-medium">
                      {typeof p.stock === 'number' ? (
                        <span className={cn(p.stock === 0 ? 'text-[var(--state-danger)]' : p.stock < 10 ? 'text-[var(--state-warning)]' : 'text-[var(--text-primary)]')}>
                          {p.stock.toLocaleString()}
                        </span>
                      ) : (
                        <span className="text-[var(--text-muted)]">-</span>
                      )}
                    </td>
                    <td className="py-3 pr-4 text-[13px] text-[var(--text-muted)] whitespace-nowrap">{formatDate(p.createdAt)}</td>
                    <td className="py-3 pr-3 text-right" onClick={(e) => e.stopPropagation()}>
                      <PermissionGate permission="products.moderate">
                        {p.isActive ? (
                          <button
                            type="button"
                            onClick={() => { setUnpublishTarget(p); setReason(''); }}
                            className="inline-flex items-center gap-1.5 rounded-xl border border-[var(--border-subtle)] px-3 py-1.5 text-[13px] font-medium text-[var(--state-danger)] hover:bg-[var(--bg-base)]"
                          >
                            <EyeOff className="w-3.5 h-3.5" strokeWidth={1.5} />
                            Unpublish
                          </button>
                        ) : (
                          <button
                            type="button"
                            onClick={() => publishMutation.mutate(p.id)}
                            disabled={publishMutation.isPending}
                            className="inline-flex items-center gap-1.5 rounded-xl border border-[var(--border-subtle)] px-3 py-1.5 text-[13px] font-medium text-[var(--text-primary)] hover:bg-[var(--bg-base)] disabled:opacity-50"
                          >
                            <RotateCcw className="w-3.5 h-3.5" strokeWidth={1.5} />
                            Publish
                          </button>
                        )}
                      </PermissionGate>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {total > PAGE_SIZE && (
          <div className="mt-4 flex items-center justify-between text-[13px] text-[var(--text-muted)]">
            <span>
              {((page - 1) * PAGE_SIZE + 1).toLocaleString()}-{Math.min(page * PAGE_SIZE, total).toLocaleString()} of {total.toLocaleString()}
            </span>
            <div className="flex items-center gap-2">
              <button
                type="button"
                disabled={page <= 1}
                onClick={() => update({ page: page - 1 > 1 ? String(page - 1) : null }, false)}
                className="rounded-xl border border-[var(--border-subtle)] px-3 py-1.5 font-medium text-[var(--text-primary)] disabled:opacity-40"
              >
                Previous
              </button>
              <span>Page {page} of {pageCount}</span>
              <button
                type="button"
                disabled={page >= pageCount}
                onClick={() => update({ page: String(page + 1) }, false)}
                className="rounded-xl border border-[var(--border-subtle)] px-3 py-1.5 font-medium text-[var(--text-primary)] disabled:opacity-40"
              >
                Next
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Unpublish confirmation */}
      {unpublishTarget && (
        <div className="fixed inset-0 !mt-0 z-50 flex items-center justify-center bg-black/40 p-4" onClick={() => setUnpublishTarget(null)}>
          <div className="w-full max-w-md rounded-3xl bg-white shadow-soft p-6" onClick={(e) => e.stopPropagation()}>
            <h3 className="text-[15px] font-semibold text-[var(--text-primary)] mb-1">Unpublish product</h3>
            <p className="text-[13px] text-[var(--text-muted)] mb-4">
              "{unpublishTarget.name}" by {unpublishTarget.businessName} will disappear from the storefront and search immediately.
              The merchant can be notified with your reason.
            </p>
            <textarea
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              rows={3}
              placeholder="Reason (optional)"
              className="w-full rounded-2xl border border-[var(--border-subtle)] bg-[var(--bg-base)] px-3 py-2 text-[13px] text-[var(--text-primary)] placeholder:text-[var(--text-muted)] focus:outline-none"
            />
            <div className="mt-5 flex justify-end gap-2">
              <button
                type="button"
                onClick={() => setUnpublishTarget(null)}
                className="rounded-xl px-4 py-2 text-[13px] font-medium text-[var(--text-secondary)] hover:bg-[var(--bg-base)]"
              >
                Cancel
              </button>
              <button
                type="button"
                disabled={unpublishMutation.isPending}
                onClick={() => unpublishMutation.mutate({ id: unpublishTarget.id, why: reason.trim() || undefined })}
                className="inline-flex items-center gap-2 rounded-xl bg-[var(--state-danger)] px-4 py-2 text-[13px] font-semibold text-white disabled:opacity-50"
              >
                {unpublishMutation.isPending && <Loader2 className="w-3.5 h-3.5 animate-spin" />}
                Unpublish
              </button>
            </div>
          </div>
        </div>
      )}

      {selected && (
        <ProductDetailModal
          businessId={selected.row.businessId}
          product={selected.full}
          onClose={() => { setSelected(null); refresh(); }}
        />
      )}
    </div>
  );
};
